import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

// Semaine Samedi→Vendredi
const getSaturdayWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0=Dim,...,6=Sam
  const diff = day === 6 ? 0 : day + 1;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};
const getFridayWeekEnd = (date) => {
  const start = getSaturdayWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

function ExpenseDateRangePicker({ dateRange, onDateRangeChange, compact = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempRange, setTempRange] = useState(dateRange);
  const [activeShortcut, setActiveShortcut] = useState('thisMonth');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectingDateType, setSelectingDateType] = useState('start');

  const shortcuts = [
    { label: "Aujourd'hui", value: 'today', getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
    { label: 'Cette semaine', value: 'thisWeek', getRange: () => ({ from: getSaturdayWeekStart(new Date()), to: getFridayWeekEnd(new Date()) }) },
    { label: 'Ce mois', value: 'thisMonth', getRange: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
    { label: 'Global', value: 'global', getRange: () => ({ from: null, to: null }) },
    { label: 'Personnalisé', value: 'custom', getRange: null }
  ];

  const handleShortcutClick = (shortcut) => {
    setActiveShortcut(shortcut.value);
    if (shortcut.value !== 'custom' && shortcut.getRange) {
      const range = shortcut.getRange();
      setTempRange(range);
    }
  };

  const handleDateSelect = (date) => {
    if (selectingDateType === 'start') {
      setTempRange({ from: date, to: null });
      setSelectingDateType('end');
    } else {
      setTempRange(prev => ({ ...prev, to: date }));
      setSelectingDateType('start');
    }
  };

  const handleConfirm = () => {
    if ((tempRange?.from && tempRange?.to) || (tempRange?.from === null && tempRange?.to === null)) {
      onDateRangeChange(tempRange);
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setTempRange(dateRange);
    setIsOpen(false);
    setActiveShortcut('thisMonth');
    setSelectingDateType('start');
  };

  const formatDateDisplay = () => {
    if (!dateRange?.from) return 'Période Globale';
    const fromStr = format(dateRange.from, 'dd MMM', { locale: fr });
    const toStr = dateRange.to ? format(dateRange.to, 'dd MMM yyyy', { locale: fr }) : '';
    return dateRange.to ? `${fromStr} – ${toStr}` : fromStr;
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDayOfWeek = getDay(monthStart);
    const weekDays = ['S', 'D', 'L', 'M', 'M', 'J', 'V'];
    const adjustedStartDay = (startDayOfWeek + 1) % 7;

    return (
      <div className="bg-gray-50 rounded-2xl p-6 mt-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronLeft size={20} />
          </button>
          <h4 className="text-xl font-bold text-gray-900">
            {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
          </h4>
          <button
            onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-3">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: adjustedStartDay }).map((_, idx) => (
            <div key={`empty-${idx}`} />
          ))}

          {daysInMonth.map((day) => {
            const isStart = tempRange?.from &&
              format(day, 'yyyy-MM-dd') === format(tempRange.from, 'yyyy-MM-dd');
            const isEnd = tempRange?.to &&
              format(day, 'yyyy-MM-dd') === format(tempRange.to, 'yyyy-MM-dd');
            const isInRange = tempRange?.from && tempRange?.to &&
              day > tempRange.from && day < tempRange.to;

            return (
              <button
                key={day.toISOString()}
                onClick={() => handleDateSelect(day)}
                className={`
                  h-12 rounded-xl font-medium text-base transition-all
                  ${isStart || isEnd
                    ? 'bg-emerald-500 text-white'
                    : isInRange
                      ? 'bg-emerald-100 text-emerald-900'
                      : 'text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>

        <div className="mt-6 text-center text-gray-600 text-sm">
          {selectingDateType === 'start' ? 'Sélectionnez la date de début' : 'Sélectionnez la date de fin'}
        </div>

        {tempRange?.from && tempRange?.to && (
          <div className="bg-white rounded-xl p-4 flex items-center gap-3 mt-4">
            <Calendar className="text-emerald-600" size={20} />
            <span className="font-medium text-gray-900 text-sm">
              {format(tempRange.from, 'dd MMM', { locale: fr })} → {format(tempRange.to, 'dd MMM yyyy', { locale: fr })}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center transition-colors cursor-pointer ${
          compact 
            ? 'gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50' 
            : 'gap-3 p-4 border-2 border-gray-200 rounded-2xl bg-white hover:border-gray-300'
        }`}
      >
        <Calendar className={`${compact ? 'w-4 h-4' : 'w-6 h-6'} text-gray-500`} />
        <span className={`text-gray-900 font-semibold flex-1 ${compact ? 'text-sm' : 'text-base'}`}>
          {formatDateDisplay()}
        </span>
        <svg className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleCancel} />
          <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-[500px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Quelle période ?</h3>
                <p className="text-gray-500 text-sm mt-1">Sélection rapide ou dates personnalisées</p>
              </div>
              <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {/* Boutons de raccourcis horizontaux */}
            <div className="flex flex-wrap gap-3 mb-4">
              {shortcuts.map(shortcut => (
                <button
                  key={shortcut.value}
                  onClick={() => handleShortcutClick(shortcut)}
                  className={`px-6 py-3 rounded-full font-medium transition-all ${activeShortcut === shortcut.value
                      ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-300'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-gray-300'
                    }`}
                >
                  {shortcut.label}
                </button>
              ))}
            </div>

            {/* Calendrier si Personnalisé est sélectionné */}
            {activeShortcut === 'custom' && renderCalendar()}

            {/* Boutons Annuler/Confirmer */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
              <Button
                variant="ghost"
                onClick={handleCancel}
                className="text-gray-700 hover:text-gray-900 px-8 font-semibold text-base"
              >
                Annuler
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!(tempRange?.from && tempRange?.to) && !(tempRange?.from === null && tempRange?.to === null)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-base font-bold disabled:opacity-50"
              >
                Confirmer
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ExpenseDateRangePicker;
